import React, {
  memo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";
import {
  FormControlStyled,
  Row,
  AdministrativeLevelLabel,
  FormControlStyledName
} from "../styled/SidePanel.style";
import SidePanelCard from "./StyledSidePanelCard";
import TypeSelect from "./TypeSelect";
import {
  getDataForInitialAdminLevels,
  getAdminLevelsBySelectedValue,
  fetchYearAndQuarterByCountry,
} from "../../services/api/mapLocation.api";
import { WebMapContext } from "../../pages/sections/web-map/WebMapGoogleMap";
import PrimarySearchBar from "../PrimarySearchBar";
import InfoToolTip from "../elements/infoTooltip";
import { LoadMapContext } from "../../pages/sections/web-map/LoadWebMap";
import { GEOHASH_MAX_LEN, MapTools } from "../../constants/WebMap";
import {
  convertPolygonToGeohash, convertGeohashToLatLng,
  generateGeohashSuggestions,
  checkGeohashInPolygon,
  getUniqueNeighbors
} from "../../utils/helper";
import { getGeohashesByCountry } from "../../services/api/country.api";

function LocationSelect({
  openedTabs,
  setOpenedTabs,
  setPosition,
  isEyeIconVisibleAsGreen,
  setIsEyeIconVisibleAsGreen,
  setIsEyeIconVisibleAsGreenForSites,
  selectedCountryPrems
}) {
  const [adminLevels1, setAdminLevels1] = useState([]);
  const [selectedAdminLevel1, setSelectedAdminLevel1] = useState(null);
  const [adminLevels2, setAdminLevels2] = useState([]);
  const [selectedAdminLevel2, setSelectedAdminLevel2] = useState(null);
  const [adminLevels3, setAdminLevels3] = useState([]);
  const [selectedAdminLevel3, setSelectedAdminLevel3] = useState(null);
  const [yearsAndQuarters, setYearsAndQuarters] = useState({});
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [quarters, setQuarters] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [isAdministrativeOpen, setIsAdministrativeOpen] = useState(false);
  const [selectedAdminLevel, setSelectedAdminLevel] = useState(null);
  const [selectedAdminLevelValue, setSelectedAdminLevelValue] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState(null);
  const [selectedGeohashLevel, setSelectedGeohashLevel] = useState(null);
  const [validGeohashes, setValidGeohashes] = useState([]);
  const [suggestedGeohashes, setSuggestedGeohashes] = useState([]);
  const [loadingGeohashes, setLoadingGeohashes] = useState([]);
  const [countryGeohashes, setCountryGeohashes] = useState([]);
  
  const { onChangeFocusedAdminLvl, onClickAdminLvlEyeIcon, appliedFilters, mapPlacement,
    selectedPolygons, getPolygon, setSelectedPolygons, globalConfig } = useContext(WebMapContext);
  const { commonFilters, commonEyeIcons, isMapsCoupled, mapView } = useContext(LoadMapContext);
  const admLevelsPerm = selectedCountryPrems?.sub_permissions?.find((p) => p.name === "ADM Levels");
  const geoHashPerm = selectedCountryPrems?.sub_permissions?.find((p) => p.name === "Geohash");
  const locationPerm = selectedCountryPrems?.sub_permissions?.find((p) => p.name === "Location");
  const user = JSON.parse(localStorage.getItem("user"));
  const permissions = user?.permissions;

  useEffect(() => {
    if (!appliedFilters.countryCode) {
      setSelectedYear();
      setSelectedQuarter();
    } else {
      getYearAndQuarter();
    }
    resetFiltersInCountryChange();
    if (searchInputValue) resetSearch();
    setIsAdministrativeOpen(false);
    setIsVisible(false);

    // fetch country geohashes
    const fetchCountryGeohashes = async () => {
      if (appliedFilters.countryCode) {
        const { data } = await getGeohashesByCountry(appliedFilters.countryCode)
        if (data) setCountryGeohashes(data);
      }
    }
    fetchCountryGeohashes();
  }, [appliedFilters.countryCode]);

  useEffect(() => {
    if (mapPlacement === 'left') {
      resetFilters();
      if (searchInputValue) resetSearch();
      if (isAdministrativeOpen) getAllAdminLevels();
      handleGreenIcons();
      handleWebMapBasedOnFilters();
    }
  }, [selectedYear, selectedQuarter]);

  useEffect(() => {
    // set right map year/quarter/adm lvls
    if (mapPlacement === 'right') handleWebMapBasedOnFilters();
  }, [
    commonFilters.countryCode, commonFilters.level, commonFilters.levelVal,
    commonFilters.adminLevel1, commonFilters.adminLevel2, commonFilters.adminLevel3,
    mapView, isMapsCoupled,
    selectedYear, selectedQuarter
  ]);

  useEffect(() => {
    if (mapPlacement === 'right') onClickAdminLvlEyeIcon(commonEyeIcons.adminLevel);
  }, [commonEyeIcons.adminLevel]);

  useEffect(() => {
    if (isVisible) {
      const { country, year, quarter, level } = appliedFilters;
      if (
        country === null ||
        year === null ||
        quarter === null ||
        level === null
      ) {
        setIsVisible(false);
        onClickAdminLvlEyeIcon(false);
      }
    }
  }, [appliedFilters]);

  useEffect(() => {
    const getAdminLevelsDataBySelectedValue = async () => {
      if (selectedAdminLevel === 'geoHashLevel' && selectedAdminLevel3 !== null) return;
      const admLvlVal = selectedAdminLevel === 'geoHashLevel'
        ? getLatLngByGeohash(selectedAdminLevelValue)
        : selectedAdminLevelValue;
      try {
        const location = {
          code: appliedFilters.countryCode,
          level: selectedAdminLevel,
          value: admLvlVal,
          prevVal: selectedAdminLevel === 'adminLevel2' ? selectedAdminLevel1 : selectedAdminLevel === 'adminLevel3' ? selectedAdminLevel2 : null,
          quarter: `${selectedQuarter}%20${selectedYear}`,
          ...(selectedAdminLevel1 && {adminLevel1: selectedAdminLevel1}),
          ...(selectedAdminLevel2 && {adminLevel2: selectedAdminLevel2})
        };
        const adminLevelsData = await getAdminLevelsBySelectedValue(location);
        if (selectedAdminLevel == "adminLevel1") {
          setAdminLevels2(adminLevelsData.data.admin.map((temp) => temp.key));
          setAdminLevels3([]);
          setValidGeohashes([]);
        } else if (selectedAdminLevel == "adminLevel2") {
          setAdminLevels3(adminLevelsData.data.admin.map((temp) => temp.key));
          setValidGeohashes([]);
          setSelectedAdminLevel1(adminLevelsData.data.level[0]);
          if (selectedAdminLevel1 == null) {
            const tempLocation = {
              code: appliedFilters.countryCode,
              level: "adminLevel1",
              value: adminLevelsData.data.level[0],
              quarter: `${selectedQuarter}%20${selectedYear}`,
              prevVal: null
            };
            handleGetAdminLevels(tempLocation).then((x) => setAdminLevels2(x));
          }
        } else if (selectedAdminLevel == "adminLevel3") {
          setSelectedAdminLevel1(adminLevelsData.data.level[0]);
          setSelectedAdminLevel2(adminLevelsData.data.level[1]);
          if (selectedAdminLevel2 == null) {
            const tempLocation = {
              code: appliedFilters.countryCode,
              level: "adminLevel2",
              value: adminLevelsData.data.level[1],
              quarter: `${selectedQuarter}%20${selectedYear}`,
              prevVal: selectedAdminLevel1 || null
            };
            handleGetAdminLevels(tempLocation).then((x) => setAdminLevels3(x));
          }
          if (selectedAdminLevel1 == null) {
            const tempLocation = {
              code: appliedFilters.countryCode,
              level: "adminLevel1",
              value: adminLevelsData.data.level[0],
              quarter: `${selectedQuarter}%20${selectedYear}`,
              prevVal: null
            };
            handleGetAdminLevels(tempLocation).then((x) => setAdminLevels2(x));
          }
        } else if (selectedAdminLevel == "geoHashLevel") {
          if (!adminLevelsData.data.level[0]) {
            toast.error('Searched geohash is located outside from the selected country', { theme: "colored" });
            setSelectedGeohashLevel(null);
            setSelectedAdminLevelValue(null);
            return;
          }
          setSelectedAdminLevel1(adminLevelsData.data.level[0]);
          setSelectedAdminLevel2(adminLevelsData.data.level[1]);
          setSelectedAdminLevel3(adminLevelsData.data.level[2]);
          if (selectedAdminLevel2 == null) {
            const tempLocation = {
              code: appliedFilters.countryCode,
              level: "adminLevel2",
              value: adminLevelsData.data.level[1],
              quarter: `${selectedQuarter}%20${selectedYear}`,
              prevVal: selectedAdminLevel1 || null
            };
            handleGetAdminLevels(tempLocation).then((x) => setAdminLevels3(x));
          }
          if (selectedAdminLevel1 == null) {
            const tempLocation = {
              code: appliedFilters.countryCode,
              level: "adminLevel1",
              value: adminLevelsData.data.level[0],
              quarter: `${selectedQuarter}%20${selectedYear}`,
              prevVal: null
            };
            handleGetAdminLevels(tempLocation).then((x) => setAdminLevels2(x));
          }
        }
      } catch (err) { }
    };
    if (
      appliedFilters.countryCode != null &&
      selectedAdminLevel != null &&
      selectedAdminLevelValue != null &&
      mapPlacement !== 'right'
    ) {
      getAdminLevelsDataBySelectedValue();
    }
    handleGreenIcons();
    if (searchInputValue) resetSearch();
  }, [selectedAdminLevel, selectedAdminLevelValue, selectedYear, selectedQuarter]);

  useEffect(() => {
    if (openedTabs.isSidePanelCardOpen) setIsAdministrativeOpen(false);
  }, [openedTabs]);

  useEffect(async () => {
    if (selectedGeohashLevel && selectedAdminLevel3) await getPolygon({
      ...appliedFilters,
      level: 'adminLevel3',
      levelVal: selectedAdminLevel3
    }, false);
    if (!selectedAdminLevel3 && selectedPolygons.length > 0) setSelectedPolygons([]);
  }, [selectedAdminLevel3]);

  useEffect(async () => {
    try {
      setLoadingGeohashes(true);
      if ((selectedAdminLevel3 || selectedGeohashLevel) &&
        selectedPolygons.length !== 0) {
        const validGeohs = [];
        let prec = 5;
        while (validGeohs.length === 0) {
          const geohs = await convertPolygonToGeohash(selectedPolygons, prec);
          validGeohs.push(...geohs);
          prec++;
          if (prec > GEOHASH_MAX_LEN) break;
        }

        if (validGeohs.length) {
          const neighbors = getUniqueNeighbors(validGeohs);
          neighbors.forEach(neighbor => {
            let sugg = generateGeohashSuggestions(neighbor);
            sugg = sugg.filter(x => checkGeohashInPolygon(x, selectedPolygons, true));
            validGeohs.push(...sugg);
          })
        }
        setValidGeohashes(validGeohs);

        if (selectedGeohashLevel?.length === 5 && suggestedGeohashes.length > 0) {
          const hints = suggestedGeohashes.filter(x => checkGeohashInPolygon(x, selectedPolygons, true))
          setSuggestedGeohashes([...hints]);
        }
      } else if ((selectedAdminLevel1 || selectedAdminLevel2) && !selectedAdminLevel3) {
        setValidGeohashes([]);
        setSuggestedGeohashes([]);
      } else if (countryGeohashes.length > 0 && !selectedAdminLevel3) {
        setValidGeohashes(countryGeohashes);
      }
      setLoadingGeohashes(false);
    } catch (e) {
      setLoadingGeohashes(false);
    }
  }, [countryGeohashes?.length, selectedPolygons, selectedAdminLevel]);

  useEffect(() => {
    if (mapPlacement === 'left') {
      const admLvls = {
        adminLevel1: selectedAdminLevel1, adminLevel2: selectedAdminLevel2,
        adminLevel3: selectedAdminLevel3, geoHashLevel: selectedGeohashLevel
      };
      if ((admLvls[selectedAdminLevel] === selectedAdminLevelValue) ||
        !selectedAdminLevel // clear levels and drawn polygon
      ) handleWebMapBasedOnFilters();
    }
  }, [selectedAdminLevel1, selectedAdminLevel2, selectedAdminLevel3,
    selectedGeohashLevel, selectedAdminLevel, selectedAdminLevelValue]);

  const filterPremittedYears = (firstObj, secondObj) => {
    const result = {};

    for (const [key, values] of Object.entries(secondObj)) {
        if (firstObj[key]) {
            const filteredValues = values.filter(value => !firstObj[key].includes(value));
            if (filteredValues.length > 0) {
                result[key] = filteredValues;
            }
        } else {
            result[key] = values;
        }
    }
    return result;
  };

  const getYearAndQuarter = async () => {
    try {
      const { data } = await fetchYearAndQuarterByCountry(appliedFilters.countryCode);
      const excludedYears =  permissions?.find((perm) => perm.country === appliedFilters.countryCode)?.years;
      const permittedYears = filterPremittedYears(excludedYears, data);
      if (permittedYears) {
        setYearsAndQuarters(permittedYears);
        if (
          typeof permittedYears === "object" &&
          permittedYears !== null &&
          Object.keys(permittedYears).length > 0
        ) {
          const resYears = Object.keys(permittedYears);
          setYears(resYears.sort((a, b) => b - a));
          if (selectedYear && resYears.includes(selectedYear)) {
            const resQuarters = permittedYears[selectedYear];
            setQuarters(resQuarters.sort((a, b) => b.localeCompare(a)));
            if (!resQuarters.includes(selectedQuarter))
              setSelectedQuarter(resQuarters[0]);
          } else {
            const resQuarters = permittedYears[resYears[0]];
            setQuarters(resQuarters.sort((a, b) => b.localeCompare(a)));
            setSelectedYear(resYears[0]);
            setSelectedQuarter(resQuarters[0]);
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getAdminLevelInitialData = async (country, adminLevel, quarter) => {
    try {
      const adminLevelInitialData = await getDataForInitialAdminLevels(
        country,
        adminLevel,
        quarter
      );
      const adminLevelsInitial = adminLevelInitialData.data.map((temp) => {
        return temp._id;
      });
      if (adminLevel === "adminLevel3") {
        setAdminLevels3(adminLevelsInitial);
        if(selectedAdminLevel3 && !adminLevelsInitial.includes(selectedAdminLevel3)) {
          setSelectedAdminLevel2(null)
        }
      } else if (adminLevel === "adminLevel2") {
        setAdminLevels2(adminLevelsInitial);
        if(selectedAdminLevel2 && !adminLevelsInitial.includes(selectedAdminLevel2)) {
          setSelectedAdminLevel2(null)
        }
      } else if (adminLevel === "adminLevel1") {
        setAdminLevels1(adminLevelsInitial);
        if(selectedAdminLevel1 && !adminLevelsInitial.includes(selectedAdminLevel1)) {
          setSelectedAdminLevel1(null)
        }
      }
    } catch (err) { }
  };

  const handleGetAdminLevels = async (tempLocation) => {
    try {
      const tempAdminLevelsData = await getAdminLevelsBySelectedValue(
        tempLocation
      );
      return tempAdminLevelsData.data.admin.map((temp) => temp.key);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAdminLevel1 = (val) => {
    if (val) {
      setSelectedAdminLevel1(val);
      setSelectedAdminLevel("adminLevel1");
      setSelectedAdminLevelValue(val);
      setSelectedAdminLevel2(null);
      setSelectedAdminLevel3(null);
      setSelectedGeohashLevel(null);
    } else {
      setSelectedAdminLevel1(null);
      setSelectedAdminLevel2(null);
      setSelectedAdminLevel3(null);
      setSelectedGeohashLevel(null);
      setSelectedAdminLevel(null);
      setSelectedAdminLevelValue(null);
      getAllAdminLevels();
    }
  };

  const handleAdminLevel2 = (val) => {
    if (val) {
      setSelectedAdminLevel2(val);
      setSelectedAdminLevel("adminLevel2");
      setSelectedAdminLevelValue(val);
      setSelectedAdminLevel3(null);
      setSelectedGeohashLevel(null)
    } else {
      setSelectedAdminLevel2(null);
      setSelectedAdminLevel3(null);
      setSelectedGeohashLevel(null)
      setSelectedAdminLevel("adminLevel1");
      setSelectedAdminLevelValue(selectedAdminLevel1);
    }
  };

  const handleAdminLevel3 = (val) => {
    if (val) {
      setSelectedAdminLevel3(val);
      setSelectedAdminLevel("adminLevel3");
      setSelectedAdminLevelValue(val);
      setSelectedGeohashLevel(null)
    } else {
      setSelectedAdminLevel3(null);
      setSelectedGeohashLevel(null);
      setSelectedAdminLevel("adminLevel2");
      setSelectedAdminLevelValue(selectedAdminLevel2);
    }
  };

  const getLatLngByGeohash = (geohash) => {
    const geoHashLatLng = convertGeohashToLatLng(geohash);
    const searchVal = encodeURIComponent(JSON.stringify(geoHashLatLng));
    return searchVal;
  }

  const handleGeoHashLevel = (val) => {
    if (val) {
      if (/[ail]/i.test(val)) {
        toast.error('Invalid Geohash', { theme: "colored" });
        return;
      }
      if (selectedPolygons.length > 0) {
        const isValid = checkGeohashInPolygon(val, selectedPolygons, true);
        if (!isValid) {
          toast.error('Searched geohash is located outside from the selected administrative levels', { theme: "colored" });
          return;
        }
      }
      setSelectedGeohashLevel(val);
      setSelectedAdminLevel("geoHashLevel");
      setSelectedAdminLevelValue(val);
    } else {
      setSelectedGeohashLevel(null);
      setSelectedAdminLevel("adminLevel3");
      setSelectedAdminLevelValue(selectedAdminLevel3);
      setSuggestedGeohashes([]);
    }
  }

  const handleYear = (val) => {
    if (val) {
      setSelectedYear(val);
      const resQuarters = yearsAndQuarters[val];
      setQuarters(resQuarters.sort((a, b) => b.localeCompare(a)));
      if (!resQuarters.includes(selectedQuarter))
        setSelectedQuarter(resQuarters[0]);
    } else {
      setSelectedYear(null);
    }
  };

  const handleQuarter = useCallback(
    (val) => {
      if (val) {
        setSelectedQuarter(val);
      } else {
        setSelectedQuarter(null);
      }
    },
    [selectedQuarter]
  );

  const handleWebMapBasedOnFilters = () => {
    onChangeFocusedAdminLvl({
      country: mapPlacement === 'left' ? appliedFilters.country : commonFilters.country,
      countryCode: mapPlacement === 'left' ? appliedFilters.countryCode : commonFilters.countryCode,
      level: mapPlacement === 'left' ? selectedAdminLevel : mapView === MapTools.SPLIT && !isMapsCoupled ? null : commonFilters.level,
      levelVal: mapPlacement === 'left' ? selectedAdminLevelValue : mapView === MapTools.SPLIT && !isMapsCoupled ? null : commonFilters.levelVal,
      year: selectedYear,
      quarter: selectedQuarter,
      adminLevel1: mapPlacement === 'left' ? selectedAdminLevel1 : mapView === MapTools.SPLIT && !isMapsCoupled ? null : commonFilters.adminLevel1,
      adminLevel2: mapPlacement === 'left' ? selectedAdminLevel2 : mapView === MapTools.SPLIT && !isMapsCoupled ? null : commonFilters.adminLevel2,
      adminLevel3: mapPlacement === 'left' ? selectedAdminLevel3 : mapView === MapTools.SPLIT && !isMapsCoupled ? null : commonFilters.adminLevel3,
      geohash: mapPlacement === 'left' ? selectedGeohashLevel : mapView === MapTools.SPLIT && !isMapsCoupled ? null : commonFilters.geohash,
    });
  };

  const onClickEyeIcon = () => {
    setIsVisible(!isVisible);
    onClickAdminLvlEyeIcon(!isVisible);
  };

  const handleSearchInputValue = (val) => {
    val != searchInputValue
      ? setSearchInputValue(val)
      : setSearchInputValue(null);
  };

  const handleSearchClick = (val) => {
    setPosition(val);
  };

  const getAllAdminLevels = () => {
    if (selectedQuarter && selectedYear) {
      const promiseArr = [];
      for (let i = 0; i < 3; i++) {
        promiseArr.push(
          getAdminLevelInitialData(
            appliedFilters.countryCode,
            `adminLevel${i + 1}`,
            `${selectedQuarter}%20${selectedYear}`
          )
        );
      }
      Promise.allSettled(promiseArr);
    }
  };

  const resetFilters = () => {
    setAdminLevels1([]);
    setAdminLevels2([]);
    setAdminLevels3([]);
    // setValidGeohashes([]);
    // setSuggestedGeohashes([]);

    // setSelectedAdminLevel1(null);
    // setSelectedAdminLevel2(null);
    // setSelectedAdminLevel3(null);
    setSelectedGeohashLevel(null)

    // setSelectedAdminLevel(null);
    // setSelectedAdminLevelValue(null);
  };

  const resetFiltersInCountryChange = () => {
    setAdminLevels1([]);
    setAdminLevels2([]);
    setAdminLevels3([]);
    setValidGeohashes([]);
    setSuggestedGeohashes([]);

    setSelectedAdminLevel1(null);
    setSelectedAdminLevel2(null);
    setSelectedAdminLevel3(null);
    setSelectedGeohashLevel(null)

    setSelectedAdminLevel(null);
    setSelectedAdminLevelValue(null);
  };

  const resetSearch = () => {
    handleSearchInputValue("");
    handleSearchClick();
  };

  const handleGreenIcons = () => {
    if (
      selectedYear &&
      selectedQuarter &&
      selectedAdminLevel &&
      selectedAdminLevelValue
    ) {
      setIsEyeIconVisibleAsGreen(true);
    } else setIsEyeIconVisibleAsGreen(false);
    // if (appliedFilters.countryCode) {
    setIsEyeIconVisibleAsGreenForSites(true);
    // }
  };

  return (
    <>
      <Grid container item style={{ padding: "0px 15px 4px" }}>
        <Row>
          <Grid container spacing={0}>
            <Grid
              container
              item
              xs={6}
              sm={6}
              md={6}
              justifyContent={"flex-start"}
            >
              <FormControlStyled high>
                <FormControlStyledName>Year</FormControlStyledName>
                <TypeSelect
                  defaultVal={"Year"}
                  options={years}
                  control={selectedYear}
                  setControl={handleYear}
                />
              </FormControlStyled>
            </Grid>
            <Grid
              container
              item
              xs={6}
              sm={6}
              md={6}
              justifyContent={"flex-start"}
              style={{ paddingLeft: "5px" }}
            >
              <FormControlStyled high>
                <FormControlStyledName>Quarter</FormControlStyledName>
                <TypeSelect
                  defaultVal={"Quarter"}
                  options={quarters}
                  control={selectedQuarter}
                  setControl={handleQuarter}
                />
              </FormControlStyled>
            </Grid>
          </Grid>
        </Row>
      </Grid>
      {selectedCountryPrems?.is_enable === true && (
        <>
          {mapPlacement !== 'right' && isAdministrativeOpen ? (
          <>
            <SidePanelCard
              title="Geographical Area"
              icon={"/assets/locationlevelwhite.png"}
              nonShow={true}
              clicked={isAdministrativeOpen}
              onClick={() => {
                setIsAdministrativeOpen(false);
              }}
              isVisibleAsGreen={isEyeIconVisibleAsGreen}
              visibilityClicked={isVisible}
              onClickVisibility={() => {
                if (
                  appliedFilters.year &&
                  appliedFilters.quarter &&
                  selectedAdminLevel
                )
                  onClickEyeIcon();
              }}
            />
            <Grid container item>
              <Grid container item style={{ padding: "5px 15px ", border: "1px solid #73AD21", margin: "6px 6px 0px", borderRadius: "4px" }}>
                <AdministrativeLevelLabel main style={{ marginBottom: "4px" }}>Filter</AdministrativeLevelLabel>
                {admLevelsPerm?.is_enable === true && (
                  <>
                    <Row style={{ marginBottom: "0px" }}>
                      <AdministrativeLevelLabel>
                        {globalConfig?.adminLevelLabels?.adm1 || "Administrative Level 1"}
                        {/* <InfoToolTip
                          adminLevelNames={[
                            "Division",
                            "Provinsi",
                            "State",
                            "Region",
                            "Province",
                          ]}
                          countries={[
                            "Bangladesh",
                            "Indonesia",
                            "Malaysia",
                            "Philippines",
                            "Cambodia, Pakistan, Sri Lanka, Thailand",
                          ]}
                          arrowTop={"-75px !important"}
                        /> */}
                      </AdministrativeLevelLabel>
                    </Row>
                    <Row>
                      <TypeSelect
                        defaultVal={`Select ${globalConfig?.adminLevelLabels?.adm1 || "Administrative Level 1"}`}
                        options={adminLevels1.sort((a, b) => a.localeCompare(b))}
                        control={selectedAdminLevel1}
                        setControl={handleAdminLevel1}
                      />
                    </Row>
                    <Row style={{ marginBottom: "0px" }}>
                      <AdministrativeLevelLabel>
                        {globalConfig?.adminLevelLabels?.adm2 || "Administrative Level 2"}
                        {/* <InfoToolTip
                          adminLevelNames={[
                            "Kabupaten",
                            "City",
                            "Province",
                            "District",
                          ]}
                          countries={[
                            "Indonesia",
                            "Pakistan",
                            "Philippines",
                            "Bangladesh, Cambodia, Malaysia, Sri Lanka, Thailand",
                          ]}
                          arrowTop={"-70px !important"}
                        /> */}
                      </AdministrativeLevelLabel>
                    </Row>
                    <Row>
                      <TypeSelect
                        defaultVal={`Select ${globalConfig?.adminLevelLabels?.adm2 || "Administrative Level 2"}`}
                        options={adminLevels2.sort((a, b) => a.localeCompare(b))}
                        control={selectedAdminLevel2}
                        setControl={handleAdminLevel2}
                      />
                    </Row>
                    <Row style={{ marginBottom: "0px" }}>
                      <AdministrativeLevelLabel>
                        {globalConfig?.adminLevelLabels?.adm3 || "Administrative Level 3"}
                        {/* <InfoToolTip
                          left={6}
                          right={5}
                          adminLevelNames={[
                            "Upazila",
                            "Commune",
                            "Kecamatan",
                            "Mukim",
                            "District",
                            "City",
                            "Divisional Secretarial",
                            "Tambon",
                          ]}
                          countries={[
                            "Bangladesh",
                            "Cambodia",
                            "Indonesia",
                            "Malaysia",
                            "Pakistan",
                            "Philippines",
                            "Sri Lanka",
                            "Thailand",
                          ]}
                          arrowTop={"-98px !important"}
                        /> */}
                      </AdministrativeLevelLabel>
                    </Row>
                    <Row>
                      <TypeSelect
                        defaultVal={`Select ${globalConfig?.adminLevelLabels?.adm3 || "Administrative Level 3"}`}
                        options={adminLevels3.sort((a, b) => a.localeCompare(b))}
                        control={selectedAdminLevel3}
                        setControl={handleAdminLevel3}
                      />
                    </Row>
                  </>
                )}
                {geoHashPerm?.is_enable === true && (
                  <>
                    <Row style={{ marginBottom: "0px" }}>
                      <AdministrativeLevelLabel>Geohash</AdministrativeLevelLabel>
                    </Row>
                    <Row>
                      <TypeSelect
                        defaultVal={"Select Geohash"}
                        options={[...(suggestedGeohashes.length > 0 ? suggestedGeohashes : validGeohashes)].sort((a, b) => a.localeCompare(b))}
                        control={selectedGeohashLevel}
                        setControl={handleGeoHashLevel}
                        options_limit={100}
                        isLoading={loadingGeohashes}
                        setInputControl={(val) => {
                          if (val.length >= 5 && val.length < GEOHASH_MAX_LEN) {
                            if ((selectedAdminLevel1 || selectedAdminLevel2) && !selectedAdminLevel3) setSuggestedGeohashes([]);
                            else if (/[ail]/i.test(val)) setSuggestedGeohashes([val]);
                            else {
                              if (countryGeohashes.length > 0 || selectedPolygons.length > 0) {
                                let hints = generateGeohashSuggestions(val);
                                hints.push(val);
                                if (selectedPolygons.length > 0) hints = hints.filter(x => checkGeohashInPolygon(x, selectedPolygons, true));
                                else if (countryGeohashes.length > 0) hints = hints.filter(x => countryGeohashes.find(y => y === x.substring(0, 5)));
                                setSuggestedGeohashes(hints);
                              }
                            }
                          } else if (val.length === 9) {
                            if (selectedPolygons.length > 0 && checkGeohashInPolygon(val, selectedPolygons, true)) setSuggestedGeohashes([val]);
                            else if (countryGeohashes.find(x => x === val.substring(0, 5))) setSuggestedGeohashes([val]);
                            else setSuggestedGeohashes([]);
                          } else if (val.length < 5) setSuggestedGeohashes([]);
                        }}
                      />
                    </Row>
                  </>
                )}
              </Grid>
              {locationPerm?.is_enable === true && (
                <Grid
                  container
                  item
                  style={{
                    textAlign: "left",
                    font: "normal normal 550 13px/18px Open Sans",
                    letterSpacing: "0px",
                    color: "black",
                    opacity: "1",
                    padding: "5px 15px ", border: "1px solid #73AD21", margin: "4px 6px 6px", borderRadius: "4px"
                  }}
                >
                  Location
                  <PrimarySearchBar
                    value={searchInputValue}
                    setControl={handleSearchInputValue}
                    handleSearchClick={handleSearchClick}
                  />
                </Grid>
              )}
              </Grid>
          </>
          ) : (
            <div style={{ marginTop: "4px", width: "100%" }}>
              {mapPlacement !== 'right' && <SidePanelCard
                title="Geographical Area"
                icon={"/assets/locationlevel.png"}
                nonShow={true}
                clicked={isAdministrativeOpen}
                onClick={() => {
                  if (appliedFilters.year && appliedFilters.quarter) {
                    setIsAdministrativeOpen(true);
                    setOpenedTabs({
                      isAdminLevelCardOpen: true,
                      isSidePanelCardOpen: false,
                    });
                    if (!selectedAdminLevel) getAllAdminLevels();
                  }
                }}
                isVisibleAsGreen={isEyeIconVisibleAsGreen}
                visibilityClicked={isVisible}
                onClickVisibility={() => {
                  if (
                    appliedFilters.year &&
                    appliedFilters.quarter &&
                    selectedAdminLevel
                  )
                    onClickEyeIcon();
                }}
              />}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default memo(LocationSelect);
